import { Stack } from '@mui/material';
import {
  useGetBenchmarksMetricsQuery,
  useGetBenchmarksOverviewQuery,
} from 'api/reports-service/guest-journey-reports';
import SearchToolbar from 'components/search/search-toolbar/search-toolbar.component';
import {
  MultipleSearchQueryProvider,
  SearchParamsProvider,
} from 'components/search/search.context';
import {
  useSearchParams,
  useSearchQuery,
} from 'components/search/search.hooks';
import { useHasAllMerchantLocationAccess } from 'hooks/authorization.hooks';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import DataPlusGate from '../data-plus/data-plus-gate.component';
import {
  BenchmarksBootstrapData,
  benchmarksFilteredSearchParams,
  useBenchmarksSearchParams,
} from '../guest-journey.hooks';
import ReportFieldsLink from '../report-fields/report-fields-link.component';
import BenchmarksBootstrap from './benchmarks-bootstrap.component';
import BenchmarksKpiCards from './benchmarks-kpi-cards.component';
import BenchmarksLockedPreview from './benchmarks-locked-preview.component';
import BenchmarksMetricsGrid from './benchmarks-metrics-grid.component';
import { BenchmarksProvider, useBenchmarksContext } from './benchmarks.context';
import { buildBrandCuisineKpiCard } from './benchmarks.utils';

/**
 * Queries only send the params on this tab's filter bar (Benchmark Year +
 * Cuisine).
 */
const filteredSearchParams = benchmarksFilteredSearchParams;

/**
 * Gate the tab behind Data Plus, then behind the bootstrap fetch (brand cuisine
 * + filter options). Search params are only built once the bootstrap resolves,
 * so the Year/Cuisine filters get valid synchronous defaults — hence the
 * provider lives below the bootstrap, not at the top.
 */
const SearchParamsWrapper = () => {
  const { t } = useTranslation();
  const hasAllLocationAccess = useHasAllMerchantLocationAccess();

  // Benchmarks is all-location-admin only (brand-level data). A location-scoped
  // admin who reaches this route by URL is bounced back to the report (the tab
  // is already hidden for them) — sc-374570.
  if (!hasAllLocationAccess) {
    return <Navigate to=".." replace />;
  }

  return (
    <DataPlusGate
      featureName={t('page.guestJourney.tabs.benchmarks.label')}
      preview={<BenchmarksLockedPreview />}
    >
      <BenchmarksBootstrap>
        {data => <BenchmarksReady data={data} />}
      </BenchmarksBootstrap>
    </DataPlusGate>
  );
};

/**
 * Mounted only after the bootstrap has all three BE values, so the search-param
 * hooks (and the queries they drive) always start from valid defaults.
 */
const BenchmarksReady = ({ data }: { data: BenchmarksBootstrapData }) => {
  const searchParams = useSearchParams(useBenchmarksSearchParams(data));
  return (
    <BenchmarksProvider value={data}>
      <SearchParamsProvider value={searchParams}>
        <Benchmarks />
      </SearchParamsProvider>
    </BenchmarksProvider>
  );
};

const Benchmarks = () => {
  const { t } = useTranslation();
  const { brandCuisine } = useBenchmarksContext();

  // Spec v0.5 splits benchmarks into two endpoints; both take the tab's filter
  // params. Overview backs the KPI cards, metrics the section grid.
  const overview = useSearchQuery(useGetBenchmarksOverviewQuery, {
    filteredSearchParams,
  });
  const metrics = useSearchQuery(useGetBenchmarksMetricsQuery, {
    filteredSearchParams,
  });

  const loading =
    overview.queryState.isFetching || metrics.queryState.isFetching;

  // The Brand Cuisine KPI is prepended FE-side (not part of the overview array);
  // its value comes from the bootstrap, so it renders immediately as card #1.
  const brandCuisineCard = buildBrandCuisineKpiCard(t, brandCuisine);

  return (
    <Stack marginTop={2} data-testid="GuestJourney.Benchmarks">
      <MultipleSearchQueryProvider value={{ queries: { overview, metrics } }}>
        <Stack spacing={2}>
          <SearchToolbar />

          <ReportFieldsLink tab="benchmarks" />

          <BenchmarksKpiCards
            leadingCard={brandCuisineCard}
            cards={overview.data?.cards}
            loading={loading}
          />

          <BenchmarksMetricsGrid
            sections={metrics.data?.sections}
            loading={loading}
          />
        </Stack>
      </MultipleSearchQueryProvider>
    </Stack>
  );
};

export default SearchParamsWrapper;
